import React, { useMemo, useRef } from "react";
import { RightSidebarProps } from "../../../types/type";
import { modifyShape } from "../../../lib/shapes";
import Text from "./text";
import Color from "./color";
import Dimensions from "./dimensions";
import ObjectControl from "./object-control";
import {
  shouldShowAlignmentControlBasedOnType,
  shouldShowAlignmentToSelectionControlBasedOnType,
  shouldShowCurvatureSettingsByAttributes,
  shouldShowDimensionsBasedOnType,
  shouldShowFillColorBasedOnType,
  shouldShowGroupControlBasedOnType,
  shouldShowLayeringControlBasedOnType,
  shouldShowRotationSettingBasedOnType,
  shouldShowStrokeSettingBasedOnType,
  shouldShowTextBasedOnType,
} from "../../../lib/utils";
import StrokeControl from "./stroke-control";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import Rotation from "./rotation";
import TextInput from "./text-input";
import { getElementAttributeForSingleObject } from "../../../lib/canvas";
import CurvatureSettings from "./curvature-settings";
import GroupControl from "./group-control";
import { useResizableSidebar } from "../../../hooks/useResizableSidebar";
import { GripVertical } from "lucide-react";
import LayeringControl from "./layering-control";
import ObjectToSelectionControl from "./object-to-selection-control";

const RightSidebar = ({
  elementAttributes,
  setElementAttributes,
  saveStateToHistory, // undo-redo
  fabricRef,
  activeObjectRef,
  isEditingRef,
  syncShapeInStorage,
  deleteShapeFromStorage,
  reorderCanvasObjects,
  suppressHistoryRef, // used to stop history while grouping/ungrouping, to not push in middle operations.
}: RightSidebarProps) => {
  const colorInputRef = useRef(null);
  const strokeInputRef = useRef(null);

  const handleInputChange = (property: string, value: string | number) => {
    if (!isEditingRef.current) isEditingRef.current = true;

    if (
      property === "width" ||
      property === "height" ||
      property === "left" ||
      property === "top" ||
      property === "fontSize" ||
      property === "strokeWidth" ||
      property === "angle" ||
      property === "arcAngle" ||
      property === "diameter" ||
      property === "startAngle"
    ) {
      value = Number(value);
    }

    const updatedShape = modifyShape({
      canvas: fabricRef.current as fabric.Canvas,
      property,
      value,
      activeObjectRef,
      syncShapeInStorage,
    });

    if (updatedShape) {
      // get new attributes, cos for height or width, or angle, or
      // text value change, we need the new attributes, after the change.
      // otherwise, if we update just the property we changed on the canvas,
      // object, the W & H controls will not reflect the new values.
      // then if user try to click on them before, deselecting or selecting,
      // the object, it will scale from the current set value in the state,
      // which cause wrong scaling issues.

      console.log("updated shape", updatedShape);

      const newAttrs: any = getElementAttributeForSingleObject(updatedShape);
      console.log("newAttrs", newAttrs);

      setElementAttributes((prev) => newAttrs);

      saveStateToHistory?.();
    }
  };

  const { width, handleMouseDown } = useResizableSidebar({
    sidebarKey: "designerRightSidebarWidth",
    direction: "right",
  });

  // memoize the content of the right sidebar to avoid re-rendering on every mouse actions
  const memoizedContent = useMemo(
    () => (
      <section
        className="z-10 flex flex-col border-t border-borderColor bg-blackLight text-white min-w-[240px] w-[240px] sticky right-0 h-full max-sm:hidden select-none"
        style={{ width: `${width}px` }}
      >
        {/* Resize handle */}
        <div
          onMouseDown={handleMouseDown}
          className="absolute flex cursor-col-resize z-40 text-primary top-[50%] -left-3 bg-blackLight rounded"
        >
          <GripVertical size={20} />
        </div>

        <div className="border-b border-borderColor p-2">
          <h3 className=" text-xs font-semibold uppercase">Design</h3>

          {!elementAttributes.type && (
            <span className="text-xs text-center text-grey-300 mt-3 ">
              Select an element to edit its properties.
            </span>
          )}
        </div>

        <ScrollArea.Root className="w-full h-full rounded overflow-hidden relative">
          <ScrollArea.Viewport className="w-full h-full rounded">
            <div className="mb-10 pb-2 mr-2">
              {shouldShowTextBasedOnType(elementAttributes?.type) && (
                <TextInput
                  isEditingRef={isEditingRef}
                  text={elementAttributes.text ?? ""}
                  handleInputChange={handleInputChange}
                />
              )}

              {shouldShowCurvatureSettingsByAttributes(elementAttributes) && (
                <CurvatureSettings
                  arcAngle={elementAttributes.arcAngle as number}
                  diameter={elementAttributes.diameter as number}
                  startAngle={elementAttributes.startAngle as number}
                  isEditingRef={isEditingRef}
                  handleInputChange={handleInputChange}
                />
              )}

              {shouldShowGroupControlBasedOnType(elementAttributes?.type) && (
                <GroupControl
                  fabricRef={fabricRef}
                  syncShapeInStorage={syncShapeInStorage}
                  deleteShapeFromStorage={deleteShapeFromStorage}
                  suppressHistoryRef={suppressHistoryRef}
                  saveStateToHistory={saveStateToHistory}
                />
              )}

              {shouldShowDimensionsBasedOnType(elementAttributes?.type) && (
                <Dimensions
                  isEditingRef={isEditingRef}
                  width={elementAttributes.width}
                  height={elementAttributes.height}
                  left={elementAttributes.left}
                  top={elementAttributes.top}
                  handleInputChange={handleInputChange}
                />
              )}

              {shouldShowAlignmentControlBasedOnType(
                elementAttributes?.type
              ) && (
                <ObjectControl
                  fabricRef={fabricRef}
                  syncShapeInStorage={syncShapeInStorage}
                  saveStateToHistory={saveStateToHistory}
                />
              )}

              {shouldShowAlignmentToSelectionControlBasedOnType(
                elementAttributes?.type
              ) && (
                <ObjectToSelectionControl
                  fabricRef={fabricRef}
                  syncShapeInStorage={syncShapeInStorage}
                  saveStateToHistory={saveStateToHistory}
                />
              )}

              {shouldShowLayeringControlBasedOnType(
                elementAttributes?.type
              ) && (
                <LayeringControl
                  fabricRef={fabricRef}
                  syncShapeInStorage={syncShapeInStorage}
                  reorderCanvasObjects={reorderCanvasObjects}
                  saveStateToHistory={saveStateToHistory}
                />
              )}

              {shouldShowRotationSettingBasedOnType(elementAttributes.type) && (
                <Rotation
                  elementAttributes={elementAttributes}
                  handleInputChange={handleInputChange}
                />
              )}

              {shouldShowTextBasedOnType(elementAttributes?.type) && (
                <Text
                  elementAttributes={elementAttributes}
                  fontFamily={elementAttributes.fontFamily || ""}
                  fontSize={elementAttributes.fontSize || 0}
                  fontWeight={elementAttributes.fontWeight || ""}
                  handleInputChange={handleInputChange}
                />
              )}

              {shouldShowFillColorBasedOnType(elementAttributes.type) && (
                <Color
                  inputRef={colorInputRef}
                  attribute={elementAttributes.fill}
                  placeholder="color (Fill)"
                  attributeType="fill"
                  handleInputChange={handleInputChange}
                />
              )}

              {shouldShowStrokeSettingBasedOnType(elementAttributes.type) && (
                <StrokeControl
                  inputRef={strokeInputRef}
                  attribute={elementAttributes.stroke}
                  elementAttributes={elementAttributes}
                  placeholder="stroke"
                  attributeType="stroke"
                  handleInputChange={handleInputChange}
                />
              )}
            </div>
          </ScrollArea.Viewport>
          <ScrollArea.Scrollbar
            className="flex select-none touch-none p-0.5 transition-colors duration-[160ms] ease-out hover:bg-black data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-2.5"
            orientation="vertical"
          >
            <ScrollArea.Thumb className="flex-1 bg-scrollbarColor rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full before:min-w-[44px] before:min-h-[44px]" />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
      </section>
    ),
    [elementAttributes, width]
  ); // only re-render when elementAttributes changes

  return memoizedContent;
};

export default RightSidebar;
